import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { RegainExportStatus } from "api/models";
import { useSelectedShop, useNotifications } from "shared/hooks";
import { downloadFile } from "shared/utils";
import { useDateRangeContext } from "../useDateRangeContext";
import { useRegainExportPolling } from "./useRegainExportPolling";
import { useRegainExportRequest } from "./useRegainExportRequest";

export const useRegainsExport = () => {
  const { t } = useTranslation();

  const { selectedShopId } = useSelectedShop();

  const { startDate, endDate } = useDateRangeContext();

  const { showNotification } = useNotifications();

  const {
    exportId,
    exportRegains,
    loading: requestLoading,
    reset: resetRequest
  } = useRegainExportRequest();

  const { data, isError, isPollingInProgress } = useRegainExportPolling(exportId);

  const handleResultDownload = useCallback(async () => {
    try {
      if (data && data.status === RegainExportStatus.READY) {
        await downloadFile(data.downloadUrl);

        resetRequest();

        showNotification("dashboardModule.regainTable.export.reportReady", "success");
      }
    } catch (error) {
      showNotification("dashboardModule.regainTable.export.reportError", "error");
    }
  }, [data, startDate, endDate, showNotification, t]);

  const executeRegainsExport = useCallback(async () => {
    try {
      showNotification("dashboardModule.regainTable.export.requestSuccess", "default");
      await exportRegains({
        shopId: selectedShopId!,
        startDate: startDate!,
        endDate: endDate!
      });
    } catch (error) {
      showNotification("dashboardModule.regainTable.export.requestError", "error");
    }
  }, [selectedShopId, startDate, endDate, exportRegains]);

  useEffect(() => {
    handleResultDownload();
  }, [data?.status]);

  useEffect(() => {
    resetRequest();
  }, [selectedShopId, startDate, endDate]);

  const loadingState =
    requestLoading ||
    (!!exportId && (data?.status !== RegainExportStatus.READY || isPollingInProgress) && !isError);

  return {
    loading: loadingState,
    executeRegainsExport: executeRegainsExport
  };
};
